import classNames from 'classnames';

import ImageProxy from '@next-image-proxy';

import type { StoreSearchApp } from '@api';

import { getMixPanelPage, MIXPANEL_EVENT_NAMES } from '@shared/api/mixPanel';
import NextLink from '@shared/common/components/NextLink';
import { useAuth } from '@shared/common/providers/AuthProvider';
import { useMixPanel } from '@shared/common/providers/MixPanelProvider';
import { APP_STATUS, getAppStatus } from '@shared/helpers/app';
import { resolveAppUrl } from '@shared/utils/resolveAppUrl';

import AppCategory from './AppCategory';
import AppStatusBadge from './AppStatusBadge';
import ViewAppLink from './ViewAppLink';

interface StoreAppCardProps {
  app: StoreSearchApp;
  className?: string;
}

const SearchCard = ({ app, className }: StoreAppCardProps) => {
  const { user } = useAuth();
  const { track } = useMixPanel();

  const {
    app_id: appId,
    app_name: appName = '',
    description = '',
    app_icon: appIcon,
    category_name: categoryName = '',
    subcategory_name: subCategoryName = '',
    production_ready: productionReady,
    app_banner: appBanner,
    status,
  } = app;

  const href = `/projects/${appId}`;
  const appStatus = getAppStatus({
    productionReady: productionReady,
    status: status || undefined,
  });

  const handleAppClick = () => {
    const page = getMixPanelPage();

    track(MIXPANEL_EVENT_NAMES.APPLICATION_CLICK, {
      distinct_id: user?.id,
      page,
      application: app.app_id,
    });
  };

  return (
    <div>
      <div className={classNames('flex', className)}>
        <NextLink href={href} className="t-subtitle2 pr-4 flex-shrink-0" onClick={handleAppClick}>
          <AppStatusBadge
            status={appStatus}
            labelClassName={appStatus === APP_STATUS.UPCOMING ? '!text-primary-600' : ''}
          >
            <ImageProxy
              alt={`${appName} Icon`}
              className="rounded-md w-11 h-11 flex-shrink-0"
              src={appIcon}
              sizes="(min-width: 768px) 50vw, (min-width: 1200px) 33vw, 100vw"
              priority
              width={44}
              height={44}
            />
          </AppStatusBadge>
        </NextLink>
        <div className="flex gap-1 flex-1 flex-col min-w-0">
          <NextLink href={href} className="truncate font-semibold" onClick={handleAppClick}>
            {appName}
          </NextLink>

          <AppCategory
            hideIcon
            className="t-body4"
            category={categoryName}
            subCategory={subCategoryName}
          />

          {description && (
            <p className="t-caption2 line-clamp-2 h-[32px] text-neutral-600">{description}</p>
          )}
        </div>

        <div className={classNames('flex flex-shrink-0 ml-4 gap-2 flex-col')}>
          <ViewAppLink href={resolveAppUrl(app.app_id, app.status)} onClick={handleAppClick} />
        </div>
      </div>

      <NextLink href={href} onClick={handleAppClick}>
        <ImageProxy
          src={appBanner}
          alt={appName}
          width={500}
          height={230}
          className="object-cover aspect-[1400/642] rounded-xl mt-5"
        />
      </NextLink>
    </div>
  );
};

export default SearchCard;
